import React from 'react';
import styles from './style.module.scss';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

import type { InsightsResponse } from '@zoocasa/go-search';
import type { ClientPreferences } from 'data/client-info-pages/types';

export interface NearbySchoolsProps {
  /** The insights data (used for top neighbourhoods / area context) */
  insightsData?: InsightsResponse;
  /** The name of the area for display */
  areaName?: string;
  /** Client preferences to tailor the section */
  clientPreferences?: ClientPreferences;
}

/**
 * Displays school & family-relevant information for an area.
 * Uses top neighbourhoods data as family-friendly neighbourhood suggestions
 * and surfaces client school-related preferences.
 *
 * Note: Actual school data is not yet available — this section uses existing
 * insights data to highlight family-relevant metrics and neighbourhoods.
 */
const NearbySchools = ({ insightsData, areaName, clientPreferences }: NearbySchoolsProps) => {
  const { formatCurrency } = useI18nFormatting();
  const { t } = useTranslation(Namespaces.COMMON);

  const medianPrice = insightsData?.MedianPurchasePrice ?? 0;
  const totalHouses = insightsData?.TotalHouses ?? 0;
  const totalTownhouses = insightsData?.TotalTownhouses ?? 0;

  const schoolLevelLabel = (level?: string) => {
    switch (level) {
      case 'elementary': return t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_SCHOOLLEVELELEMENTARY, { defaultValue: 'Elementary Schools' });
      case 'middle': return t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_SCHOOLLEVELMIDDLE, { defaultValue: 'Middle Schools' });
      case 'high': return t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_SCHOOLLEVELHIGH, { defaultValue: 'High Schools' });
      case 'all': return t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_SCHOOLLEVELALL, { defaultValue: 'All School Levels' });
      default: return t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_SCHOOLLEVELDEFAULT, { defaultValue: 'Schools' });
    }
  };

  const familyFriendlyListings = totalHouses + totalTownhouses;

  return (
    <section className={styles.component}>
      <h2 className={styles.heading}>
        {areaName
          ? t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_HEADINGWITHAREA, { areaName, defaultValue: 'Schools & Family Living — {{areaName}}' })
          : t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_HEADING, { defaultValue: 'Schools & Family Living' })}
      </h2>
      <p className={styles.description}>
        {t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_DESCRIPTION, { defaultValue: 'Key information for families looking to settle in this area.' })}
      </p>

      <div className={styles.infoGrid}>
        {/* School Priority Card */}
        {clientPreferences?.school_district_priority && (
          <div className={styles.infoCard}>
            <div className={styles.cardIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>
              {schoolLevelLabel(clientPreferences?.school_level_preference)}
            </h3>
            <p className={styles.cardText}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_SCHOOLPRIORITYTEXT, { defaultValue: 'School district quality is a priority for this search. The top neighbourhoods below are popular residential areas — ask your agent about specific school catchment zones and ratings.' })}
            </p>
          </div>
        )}

        {/* Family-Friendly Properties */}
        {familyFriendlyListings > 0 && (
          <div className={styles.infoCard}>
            <div className={styles.cardIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_FAMILYSIZEDHOMESTITLE, { defaultValue: 'Family-Sized Homes' })}</h3>
            <p className={styles.cardText}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_FAMILYHOMESBASE, { count: familyFriendlyListings, defaultValue: '{{count}} houses and townhouses currently listed' })}
              {medianPrice > 0 && t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_FAMILYHOMESWITHPRICESUFFIX, { price: formatCurrency(medianPrice), defaultValue: ' with a median purchase price of {{price}}' })}.
              {clientPreferences?.min_bedrooms && clientPreferences.min_bedrooms >= 3 &&
                t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_FAMILYHOMESBEDROOMSSUFFIX, { count: clientPreferences.min_bedrooms, defaultValue: ' Filtered for {{count}}+ bedrooms.' })
              }
            </p>
          </div>
        )}

        {/* Children Info */}
        {clientPreferences?.has_children && clientPreferences?.children_ages && clientPreferences.children_ages.length > 0 && (
          <div className={styles.infoCard}>
            <div className={styles.cardIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_CHILDRENAGESTITLE, { defaultValue: 'Children\'s Ages' })}</h3>
            <p className={styles.cardText}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_CHILDRENAGESTEXT, { ages: clientPreferences.children_ages.join(', '), defaultValue: 'Ages: {{ages}} — your agent can help identify the best school catchment areas and family-oriented community programs.' })}
            </p>
          </div>
        )}

        {/* Parking / Drives Info */}
        {clientPreferences?.parking_required && (
          <div className={styles.infoCard}>
            <div className={styles.cardIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_PARKINGAVAILABLETITLE, { defaultValue: 'Parking Available' })}</h3>
            <p className={styles.cardText}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_PARKINGAVAILABLETEXT, { defaultValue: 'Parking is required — family homes in this area typically include driveways or garage parking. Your agent can confirm parking details for specific listings.' })}
            </p>
          </div>
        )}
      </div>

      {/* Tip Box */}
      <div className={styles.tipBox}>
        <div className={styles.tipHeading}>
          {t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_TIPHEADING, { defaultValue: '💡 Family Home-Buying Tip' })}
        </div>
        <p className={styles.tipText}>
          {t(LOCALE_COMMON_PATHS.CITYINFO_NEARBYSCHOOLS_TIPTEXT, { defaultValue: 'When evaluating neighbourhoods for your family, consider proximity to schools, parks, and community centres. Ask your agent about school boundary maps, walkability to nearby amenities, and family-friendly community events in each neighbourhood.' })}
        </p>
      </div>
    </section>
  );
};

export default NearbySchools;
